import React, { useEffect, useState } from 'react'
import ButtonStartIcon from '../../../../../button/ButtonStartIcon'
import Typography from '@mui/material/Typography'
import InputField from '../../../../../FormControl/input/InputField'
import Dialog from '../../../../../dialog/Dialog'
import useYears from '../../../../../../hooks/setting/useYears'
import { Alert, Switch, Tooltip } from '@mui/material'
import useFetchData from '../../../../../../hooks/useFetchData'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSelector } from 'react-redux'

const YearDialog = ({ show, onClose, type, data = {} }) => {
  const { errors: errorsData, createYear, updateYear, deleteYear } = useYears()
  const [form, setInputForm] = useState({})
  const [errors, setInputErrors] = useState(errorsData)
  const [loading, toggleLoading] = useState(false)
  const { isLoading: isLoadingMaxPosition, data: dataMaxPosition } = useFetchData('setting/year/max-position')
  const sessionUserRoles = useSelector((state) => state.session?.user?.roles?.map((item) => item.id) || [])
  const permission = useSelector((state) => state?.session?.permissions?.year || {})
  const onChange = (e) => {
    const { name, value, checked } = e.target
    if (name === 'isHidden') {
      setInputForm({ ...form, [name]: checked })
      setInputErrors({ ...errors, [name]: null })
    } else {
      setInputForm({ ...form, [name]: value })
      setInputErrors({ ...errors, [name]: null })
    }
  }

  const onSave = async (e) => {
    if (!['create', 'edit', 'delete'].includes(type)) {
      setInputErrors({ message: 'Invalid type' })
      return
    }
    e.preventDefault()
    toggleLoading(true)
    try {
      let result = { error: false }
      if (type === 'create') {
        result = await createYear(form)
      }
      if (type === 'edit') {
        result = await updateYear(form._id, form)
      }
      if (type === 'delete') {
        result = await deleteYear(form._id)
      }
      if (result.error) {
        setInputErrors(result)
      } else {
        onClose()
      }
    } finally {
      toggleLoading(false)
    }
  }

  useEffect(() => {
    setInputErrors(errorsData)
  }, [errorsData])

  useEffect(() => {
    setInputForm(data || {})
  }, [data])

  useEffect(() => {
    if (isLoadingMaxPosition) return
    if (type === 'create' && dataMaxPosition) {
      setInputForm((old) => ({ ...old, position: dataMaxPosition }))
    }
  }, [isLoadingMaxPosition])

  const Actions = (
    <>
      <ButtonStartIcon
        onClick={onClose}
        color='primary'
        variant='outlined'
        disabled={loading}
        startIcon={['fal', 'times']}
      >
        Cancel
      </ButtonStartIcon>
      {['create', 'edit', 'delete'].includes(type) && (
        <ButtonStartIcon
          onClick={onSave}
          color='primary'
          variant='contained'
          disabled={loading}
          loading={loading}
          startIcon={['fal', 'check']}
        >
          {type === 'delete' ? 'Confirm' : 'Save'}
        </ButtonStartIcon>
      )}
    </>
  )

  return (
    <Dialog id={`${type}-year`} show={show} title={`${type} year`} onClose={onClose} actions={Actions}>
      {type === 'delete' ? (
        <Typography>
          Are you sure you want to delete the year: <b>{form.name}</b>?
        </Typography>
      ) : (
        <div className='flex flex-col gap-4 py-4'>
          <div className='grid grid-cols-3 items-center gap-4'>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Is hidden
            </Typography>
            <div className='col-span-2'>
              <Switch
                checked={!!form.isHidden || false}
                name='isHidden'
                defaultValue={form.isHidden || false}
                readOnly={
                  type === 'preview' || (type === 'edit' && !permission?.updated?.includes('position'))
                }
                onChange={
                  type === 'preview' || (type === 'edit' && !permission?.updated?.includes('position'))
                    ? undefined
                    : onChange
                }
              />
            </div>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Position
            </Typography>
            <div className='col-span-2'>
              {type === 'preview' ? (
                <Typography>{form.position}</Typography>
              ) : type === 'edit' && !permission?.updated?.includes('position') ? (
                <Typography>{form.position}</Typography>
              ) : (
                <InputField
                  id='position'
                  name='position'
                  value={form.position}
                  error={errors?.position}
                  helperText={errors?.position}
                  onChange={onChange}
                  type='number'
                  fullWidth
                />
              )}
            </div>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Name
            </Typography>
            <div className='col-span-2'>
              {type === 'preview' ? (
                <Typography>{form.name}</Typography>
              ) : type === 'edit' && !permission?.updated?.includes('basic-information') ? (
                <Typography>{form.name}</Typography>
              ) : (
                <InputField
                  id='name'
                  name='name'
                  value={form.name}
                  error={errors?.name}
                  helperText={errors?.name}
                  onChange={onChange}
                  fullWidth
                />
              )}
            </div>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Short Name
            </Typography>
            <div className='col-span-2'>
              {type === 'preview' ? (
                <Typography>{form.shortName}</Typography>
              ) : type === 'edit' && !permission?.updated?.includes('basic-information') ? (
                <Typography>{form.name}</Typography>
              ) : (
                <InputField
                  id='shortName'
                  name='shortName'
                  value={form.shortName}
                  error={errors?.shortName}
                  helperText={errors?.shortName}
                  onChange={onChange}
                  fullWidth
                />
              )}
            </div>
            {sessionUserRoles.includes('Maestro') && (
              <>
                <div className='flex gap-2 items-center justify-end'>
                  <Tooltip arrow title='This name will be used to import data from other sources'>
                    <FontAwesomeIcon icon={['fal', 'question-circle']} className='text-primary' />
                  </Tooltip>
                  <Typography
                    variant='inherit'
                    className='font-bold'
                    color='primary'
                    align='right'
                    component=''
                  >
                    Import Name
                  </Typography>
                </div>

                <div className='col-span-2'>
                  {type === 'preview' ? (
                    <Typography>{form.importName}</Typography>
                  ) : type === 'edit' && !permission?.updated?.includes('basic-information') ? (
                    <Typography>{form.name}</Typography>
                  ) : (
                    <InputField
                      id='importName'
                      name='importName'
                      value={form.importName}
                      error={errors?.importName}
                      helperText={errors?.importName}
                      onChange={onChange}
                      fullWidth
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {errors && errors.message && (
        <Alert severity='error' onClose={() => setInputErrors({ message: null })}>
          {errors.message}
        </Alert>
      )}
    </Dialog>
  )
}

export default YearDialog
